import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, Subscription } from 'rxjs';

import { DiaryType } from '../../../../data/tracker/domain/diary_type/diary_type';
import {
  Tracker,
  TrackerProps,
} from '../../../../data/tracker/domain/tracker/tracker';
import { TrackerType } from '../../../../data/tracker/domain/tracker/tracker_type';
import { PatientInfoViewModel } from '../../../patient/patient.facade';
import { NewTrackerModalFacade } from './new-tracker-modal.facade';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-new-tracker-modal',
  templateUrl: './new-tracker-modal.component.html',
  styleUrls: ['./new-tracker-modal.component.scss'],
  providers: [NewTrackerModalFacade],
})
export class NewTrackerModalComponent implements OnInit, OnDestroy {
  @Input() type: DiaryType;
  @Input() diaryTypes: DiaryType[];
  @Input() tracker?: Tracker;
  @Output() cancel = new EventEmitter();
  @Output() validate = new EventEmitter<TrackerProps>();
  public newTrackerForm: FormGroup;
  public isLoading = false;
  public error?: string;

  private patientStatusSubscription?: Subscription;
  private patientStatus$: Observable<PatientInfoViewModel>;

  constructor(
    private bsModalRef: BsModalRef,
    private facade: NewTrackerModalFacade,
  ) {
    this.patientStatus$ = this.facade.patientStatus$;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.patientStatusSubscription = this.patientStatus$.subscribe((state) => {
      // this.patient = state.patient;
    });
  }

  ngOnDestroy(): void {
    this.patientStatusSubscription?.unsubscribe();
  }

  ngOnInit(): void {
    if (this.tracker) {
      this.type = this.diaryTypes.filter(
        (t) => t.name === this.tracker?.diaryType,
      )[0];
    }
    this.newTrackerForm = new FormGroup({
      type: new FormControl({ value: this.type.label, disabled: true }, [
        Validators.required,
      ]),
      name: new FormControl(
        { value: this.tracker?.name ?? '', disabled: this.tracker?.protected },
        [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(30),
        ],
      ),
      rangeType: new FormControl(
        { value: this.tracker?.type ?? '', disabled: this.tracker?.protected },
        [Validators.required],
      ),
      minValue: new FormControl(
        { value: this.tracker?.min ?? '', disabled: this.tracker?.protected },
        [],
      ),
      maxValue: new FormControl(
        { value: this.tracker?.max ?? '', disabled: this.tracker?.protected },
        [Validators.required],
      ),
    });
  }

  hideModal() {
    this.bsModalRef.hide();
  }

  createTracker() {
    const trackerName = this.newTrackerForm.get('name')?.value;
    const rangeType = this.newTrackerForm.get('rangeType')?.value;
    const minValue =
      this.newTrackerForm.get('minValue') &&
      this.newTrackerForm.get('minValue')?.value
        ? Number(this.newTrackerForm.get('minValue')?.value)
        : undefined;
    const maxValue =
      this.newTrackerForm.get('maxValue') &&
      this.newTrackerForm.get('maxValue')?.value
        ? Number(this.newTrackerForm.get('maxValue')?.value)
        : undefined;
    if (!trackerName || !rangeType || !maxValue) {
      return;
    }
    this.isLoading = true;
    this.error = undefined;
    this.validate.emit({
      diaryType: this.type.name,
      min: minValue ?? 0,
      max: maxValue,
      steps: this.calculSteps(rangeType, minValue, maxValue),
      name: trackerName,
      type: rangeType,
      protected: false,
    } as TrackerProps);
    this.hideModal();
    this.isLoading = false;
  }

  updateTracker() {
    const trackerName = this.newTrackerForm.get('name')?.value;
    const rangeType = this.newTrackerForm.get('rangeType')?.value;
    const minValue =
      this.newTrackerForm.get('minValue') &&
      this.newTrackerForm.get('minValue')?.value
        ? Number(this.newTrackerForm.get('minValue')?.value)
        : undefined;
    const maxValue =
      this.newTrackerForm.get('maxValue') &&
      this.newTrackerForm.get('maxValue')?.value
        ? Number(this.newTrackerForm.get('maxValue')?.value)
        : undefined;
    if (!trackerName) {
      return;
    }
    this.isLoading = true;
    this.error = undefined;
    const result = {
      ...this.tracker?.props,
      name: trackerName,
      min: minValue ?? 0,
      max: maxValue,
      steps: this.calculSteps(rangeType, minValue, maxValue),
    } as TrackerProps;
    this.validate.emit(result);
    this.isLoading = false;
    this.hideModal();
  }

  get typeInvalid(): boolean | undefined {
    return (
      !this.newTrackerForm.get('type')?.valid &&
      (this.newTrackerForm.get('type')?.touched ||
        this.newTrackerForm.get('type')?.dirty)
    );
  }

  get nameInvalid(): boolean | undefined {
    return (
      !this.newTrackerForm.get('name')?.valid &&
      (this.newTrackerForm.get('name')?.touched ||
        this.newTrackerForm.get('name')?.dirty)
    );
  }

  get rangeTypeInvalid(): boolean | undefined {
    return (
      !this.newTrackerForm.get('rangeType')?.valid &&
      (this.newTrackerForm.get('rangeType')?.touched ||
        this.newTrackerForm.get('rangeType')?.dirty)
    );
  }

  get maxValueInvalid(): boolean | undefined {
    return (
      (!this.newTrackerForm.get('maxValue')?.valid &&
        (this.newTrackerForm.get('maxValue')?.touched ||
          this.newTrackerForm.get('maxValue')?.dirty)) ||
      (this.isInvalidMinMax(
        Number(this.newTrackerForm.get('minValue')?.value),
        Number(this.newTrackerForm.get('maxValue')?.value),
      ) &&
        (this.newTrackerForm.get('maxValue')?.touched ||
          this.newTrackerForm.get('maxValue')?.dirty))
    );
  }

  get minValueInvalid(): boolean | undefined {
    return (
      !this.newTrackerForm.get('minValue')?.valid &&
      (this.newTrackerForm.get('minValue')?.touched ||
        this.newTrackerForm.get('minValue')?.dirty)
    );
  }

  private isInvalidMinMax(min: number, max: number): boolean {
    return min >= max;
  }

  private calculSteps(
    rangeType: TrackerType,
    minValue: number | undefined,
    maxValue: number | undefined,
  ): number {
    if (maxValue && maxValue > (minValue ?? 0)) {
      switch (rangeType) {
        case TrackerType.Note:
          return maxValue - (minValue ?? 0);
        case TrackerType.Number:
          if (maxValue % 60 === 0) {
            return (maxValue - (minValue ?? 0)) / 5;
          } else {
            return maxValue > 50
              ? (maxValue - (minValue ?? 0)) / 10
              : maxValue - (minValue ?? 0);
          }
        case TrackerType.Percent:
          if ((maxValue - (minValue ?? 0)) % 10 === 0) {
            return (maxValue - (minValue ?? 0)) / 10;
          } else if ((maxValue - (minValue ?? 0)) % 5 === 0) {
            return (maxValue - (minValue ?? 0)) / 5;
          } else if ((maxValue - (minValue ?? 0)) % 2 === 0) {
            return (maxValue - (minValue ?? 0)) / 2;
          } else {
            return maxValue - (minValue ?? 0);
          }
      }
    }
    return 0;
  }
}
